import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * /admin/v1/eval/* API 客户端 · M07 批量回归（S2 Day 8b）
 *
 * 承接：apps/api/src/csa_api/admin/eval_admin.py（Day 8a · 2 endpoint · PR #27）
 * 类型来源：与 M02/M03 同 · 临时手写 · S2 PR-X 切 openapi-typescript
 */
import type { components } from "../../../../../packages/shared/src/api-types";

import { apiClient } from "@/lib/api-client";

type ApiSchemas = components["schemas"];

export const DEFAULT_EVAL_MERCHANT_ID = "mer_nY0bZmLI";

// ─── DTO（与后端 Pydantic 同源） ─────────────────────────────────────────

export interface RegressionSample {
	id: string;
	message: string;
	expected_intent: string;
	expected_pb: string | null;
}

export interface SamplesListResponse {
	version: string;
	author: string;
	total: number;
	samples: RegressionSample[];
}

export type RegressionMode = "intent" | "playbook" | "both";

export interface RegressionRequest {
	mode?: RegressionMode;
	samples?: RegressionSample[] | null;
}

export interface PerSampleResult {
	sample_id: string;
	message: string;
	expected_intent: string;
	actual_intent: string;
	intent_match: boolean;
	intent_confidence: number;
	expected_pb: string | null;
	actual_pb: string | null;
	pb_match: boolean | null;
	badcase_candidate: boolean;
	root_cause_hint: string | null;
	responsible_asset: string | null;
	badcase_reason: string | null;
}

export interface ConfusionMatrixCell {
	expected: string;
	actual: string;
	count: number;
}

export interface RegressionResponse {
	mode: RegressionMode;
	total: number;
	intent_passed: number;
	intent_hit_rate: number;
	pb_total_evaluable: number;
	pb_passed: number;
	pb_hit_rate: number;
	confusion_matrix: ConfusionMatrixCell[];
	per_sample: PerSampleResult[];
}

export type EvalRunCreateRequest = ApiSchemas["EvalRunCreateRequest"];
export type EvalRunItem = ApiSchemas["EvalRunItem"];
export type EvalRunComparePersistRequest = ApiSchemas["EvalRunComparePersistRequest"];
export type EvalRunCompareRequest = ApiSchemas["EvalRunCompareRequest"];
export type EvalRunCompareResponse = ApiSchemas["EvalRunCompareResponse"];
export type EvalRunCompareSnapshotItem = ApiSchemas["EvalRunCompareSnapshotItem"];
export type EvalRunCompareSnapshotListResponse = ApiSchemas["EvalRunCompareSnapshotListResponse"];
export type EvalRunListResponse = ApiSchemas["EvalRunListResponse"];
export type EvalRunResponse = ApiSchemas["EvalRunResponse"];
export type EvalSampleResultItem = ApiSchemas["EvalSampleResultItem"];
export type EvalSampleResultListResponse = ApiSchemas["EvalSampleResultListResponse"];
export type AssetChangeAuditItem = ApiSchemas["AssetChangeAuditItem"];
export type AssetChangeAuditListResponse = ApiSchemas["AssetChangeAuditListResponse"];
export type BadcaseEventItem = ApiSchemas["BadcaseEventItem"];
export type BadcaseEventListResponse = ApiSchemas["BadcaseEventListResponse"];
export type BadcaseItem = ApiSchemas["BadcaseItem"];
export type BadcaseListResponse = ApiSchemas["BadcaseListResponse"];
export type BadcaseAutoCloseRequest = ApiSchemas["BadcaseAutoCloseRequest"];
export type BadcaseExecuteRerunRequest = ApiSchemas["BadcaseExecuteRerunRequest"];
export type BadcaseExecuteRerunResponse = ApiSchemas["BadcaseExecuteRerunResponse"];
export type BadcaseGraduationRequest = ApiSchemas["BadcaseGraduationRequest"];
export type BadcaseRegressionWritebackRequest = ApiSchemas["BadcaseRegressionWritebackRequest"];
export type BadcaseRegressionWritebackResponse = ApiSchemas["BadcaseRegressionWritebackResponse"];
export type BadcaseRerunRequest = ApiSchemas["BadcaseRerunRequest"];
export type BadcaseUpdateRequest = ApiSchemas["BadcaseUpdateRequest"];
export type EvalRegressionBatchCreateRequest = ApiSchemas["EvalRegressionBatchCreateRequest"];
export type EvalRegressionBatchCloseBadcasesRequest =
	ApiSchemas["EvalRegressionBatchCloseBadcasesRequest"];
export type EvalRegressionBatchCloseBadcasesResponse =
	ApiSchemas["EvalRegressionBatchCloseBadcasesResponse"];
export type EvalRegressionBatchCloseDecision = ApiSchemas["EvalRegressionBatchCloseDecision"];
export type EvalRegressionBatchExecuteRequest = ApiSchemas["EvalRegressionBatchExecuteRequest"];
export type EvalRegressionBatchExecuteResponse = ApiSchemas["EvalRegressionBatchExecuteResponse"];
export type EvalRegressionBatchItem = ApiSchemas["EvalRegressionBatchItem"];
export type EvalRegressionBatchListResponse = ApiSchemas["EvalRegressionBatchListResponse"];
export type RegressionDatasetSampleItem = ApiSchemas["RegressionDatasetSampleItem"];
export type RegressionDatasetSampleListResponse = ApiSchemas["RegressionDatasetSampleListResponse"];

// ─── hooks ───────────────────────────────────────────────────────────────

export function useEvalSamples() {
	return useQuery<SamplesListResponse>({
		queryKey: ["admin", "eval", "samples"],
		queryFn: async () => {
			const { data } = await apiClient.get<SamplesListResponse>("/admin/v1/eval/samples");
			return data;
		},
		staleTime: 60 * 60 * 1000, // 金样本 · 1h staleTime
	});
}

export function useRegress() {
	return useMutation<RegressionResponse, unknown, RegressionRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<RegressionResponse>("/admin/v1/eval/regress", body);
			return data;
		},
	});
}

export function useEvalRuns(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	return useQuery<EvalRunListResponse>({
		queryKey: ["admin", "eval", "runs", merchantId, limit],
		queryFn: async () => {
			const { data } = await apiClient.get<EvalRunListResponse>("/admin/v1/eval/runs", {
				params: { merchant_id: merchantId, limit },
			});
			return data;
		},
		staleTime: 30 * 1000,
	});
}

export function useEvalRunSamples(evalRunId: string | null) {
	return useQuery<EvalSampleResultListResponse>({
		queryKey: ["admin", "eval", "runs", evalRunId, "samples"],
		enabled: evalRunId !== null,
		queryFn: async () => {
			const { data } = await apiClient.get<EvalSampleResultListResponse>(
				`/admin/v1/eval/runs/${evalRunId}/samples`,
			);
			return data;
		},
		staleTime: 30 * 1000,
	});
}

export function useAssetChangeAudits(
	merchantId = DEFAULT_EVAL_MERCHANT_ID,
	limit = 5,
	relatedEvalRunId: string | null = null,
) {
	return useQuery<AssetChangeAuditListResponse>({
		queryKey: ["admin", "eval", "asset-change-audits", merchantId, limit, relatedEvalRunId],
		queryFn: async () => {
			const { data } = await apiClient.get<AssetChangeAuditListResponse>(
				"/admin/v1/eval/asset-change-audits",
				{
					params: {
						merchant_id: merchantId,
						limit,
						...(relatedEvalRunId ? { related_eval_run_id: relatedEvalRunId } : {}),
					},
				},
			);
			return data;
		},
		staleTime: 30 * 1000,
	});
}

export function useCreateEvalRun(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	const queryClient = useQueryClient();
	return useMutation<EvalRunResponse, unknown, EvalRunCreateRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<EvalRunResponse>("/admin/v1/eval/runs", body);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "runs", merchantId, limit],
			});
		},
	});
}

export function useEvalRunCompare(evalRunId: string | null, body: EvalRunCompareRequest = {}) {
	return useQuery<EvalRunCompareResponse>({
		queryKey: ["admin", "eval", "runs", evalRunId, "compare", body.baseline_run_id ?? null],
		enabled: evalRunId !== null,
		queryFn: async () => {
			const { data } = await apiClient.post<EvalRunCompareResponse>(
				`/admin/v1/eval/runs/${evalRunId}/compare`,
				body,
			);
			return data;
		},
		staleTime: 30 * 1000,
	});
}

export function useEvalRunCompareSnapshots(
	merchantId = DEFAULT_EVAL_MERCHANT_ID,
	currentRunId: string | null = null,
	limit = 5,
) {
	return useQuery<EvalRunCompareSnapshotListResponse>({
		queryKey: ["admin", "eval", "compare-snapshots", merchantId, currentRunId, limit],
		enabled: currentRunId !== null,
		queryFn: async () => {
			const { data } = await apiClient.get<EvalRunCompareSnapshotListResponse>(
				"/admin/v1/eval/compare",
				{
					params: {
						merchant_id: merchantId,
						current_run_id: currentRunId,
						limit,
					},
				},
			);
			return data;
		},
		staleTime: 30 * 1000,
	});
}

export function usePersistEvalRunCompare(
	merchantId = DEFAULT_EVAL_MERCHANT_ID,
	currentRunId: string | null = null,
	limit = 5,
) {
	const queryClient = useQueryClient();
	return useMutation<
		EvalRunCompareSnapshotItem,
		unknown,
		{ evalRunId: string; body: EvalRunComparePersistRequest }
	>({
		mutationFn: async ({ evalRunId, body }) => {
			const { data } = await apiClient.post<EvalRunCompareSnapshotItem>(
				`/admin/v1/eval/runs/${evalRunId}/compare/snapshots`,
				body,
			);
			return data;
		},
		onSuccess: (snapshot) => {
			queryClient.setQueryData<EvalRunCompareSnapshotListResponse>(
				["admin", "eval", "compare-snapshots", merchantId, currentRunId, limit],
				(previous) => {
					const existingItems = previous?.items ?? [];
					const items = [
						snapshot,
						...existingItems.filter((item) => item.compare_id !== snapshot.compare_id),
					].slice(0, limit);
					return { items, total: Math.max(previous?.total ?? 0, items.length) };
				},
			);
		},
	});
}

export function useBadcases(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	return useQuery<BadcaseListResponse>({
		queryKey: ["admin", "eval", "badcases", merchantId, limit],
		queryFn: async () => {
			const { data } = await apiClient.get<BadcaseListResponse>("/admin/v1/eval/badcases", {
				params: { merchant_id: merchantId, limit },
			});
			return data;
		},
		staleTime: 30 * 1000,
	});
}

export function useBadcaseEvents(badcaseId: string | null) {
	return useQuery<BadcaseEventListResponse>({
		queryKey: ["admin", "eval", "badcases", badcaseId, "events"],
		enabled: badcaseId !== null,
		queryFn: async () => {
			const { data } = await apiClient.get<BadcaseEventListResponse>(
				`/admin/v1/eval/badcases/${badcaseId}/events`,
			);
			return data;
		},
		staleTime: 30 * 1000,
	});
}

export function useUpdateBadcase(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	const queryClient = useQueryClient();
	return useMutation<BadcaseItem, unknown, { badcaseId: string; body: BadcaseUpdateRequest }>({
		mutationFn: async ({ badcaseId, body }) => {
			const { data } = await apiClient.patch<BadcaseItem>(
				`/admin/v1/eval/badcases/${badcaseId}`,
				body,
			);
			return data;
		},
		onSuccess: (updated) => {
			queryClient.setQueryData<BadcaseListResponse>(
				["admin", "eval", "badcases", merchantId, limit],
				(previous) =>
					previous
						? {
								...previous,
								items: previous.items.map((item) =>
									item.badcase_id === updated.badcase_id ? updated : item,
								),
							}
						: previous,
			);
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "badcases", updated.badcase_id, "events"],
			});
		},
	});
}

export function useRecordBadcaseRerun(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	const queryClient = useQueryClient();
	return useMutation<BadcaseItem, unknown, { badcaseId: string; body: BadcaseRerunRequest }>({
		mutationFn: async ({ badcaseId, body }) => {
			const { data } = await apiClient.post<BadcaseItem>(
				`/admin/v1/eval/badcases/${badcaseId}/rerun`,
				body,
			);
			return data;
		},
		onSuccess: (updated) => {
			queryClient.setQueryData<BadcaseListResponse>(
				["admin", "eval", "badcases", merchantId, limit],
				(previous) =>
					previous
						? {
								...previous,
								items: previous.items.map((item) =>
									item.badcase_id === updated.badcase_id ? updated : item,
								),
							}
						: previous,
			);
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "badcases", updated.badcase_id, "events"],
			});
		},
	});
}

export function useExecuteBadcaseRerun(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	const queryClient = useQueryClient();
	return useMutation<
		BadcaseExecuteRerunResponse,
		unknown,
		{ badcaseId: string; body: BadcaseExecuteRerunRequest }
	>({
		mutationFn: async ({ badcaseId, body }) => {
			const { data } = await apiClient.post<BadcaseExecuteRerunResponse>(
				`/admin/v1/eval/badcases/${badcaseId}/rerun/execute`,
				body,
			);
			return data;
		},
		onSuccess: (response) => {
			const updated = response.badcase;
			queryClient.setQueryData<BadcaseListResponse>(
				["admin", "eval", "badcases", merchantId, limit],
				(previous) =>
					previous
						? {
								...previous,
								items: previous.items.map((item) =>
									item.badcase_id === updated.badcase_id ? updated : item,
								),
							}
						: previous,
			);
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "badcases", updated.badcase_id, "events"],
			});
		},
	});
}

export function useGraduateBadcase(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	const queryClient = useQueryClient();
	return useMutation<BadcaseItem, unknown, { badcaseId: string; body: BadcaseGraduationRequest }>({
		mutationFn: async ({ badcaseId, body }) => {
			const { data } = await apiClient.post<BadcaseItem>(
				`/admin/v1/eval/badcases/${badcaseId}/graduate`,
				body,
			);
			return data;
		},
		onSuccess: (updated) => {
			queryClient.setQueryData<BadcaseListResponse>(
				["admin", "eval", "badcases", merchantId, limit],
				(previous) =>
					previous
						? {
								...previous,
								items: previous.items.map((item) =>
									item.badcase_id === updated.badcase_id ? updated : item,
								),
							}
						: previous,
			);
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "badcases", updated.badcase_id, "events"],
			});
		},
	});
}

export function useWritebackBadcaseRegression(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	const queryClient = useQueryClient();
	return useMutation<
		BadcaseRegressionWritebackResponse,
		unknown,
		{ badcaseId: string; body: BadcaseRegressionWritebackRequest }
	>({
		mutationFn: async ({ badcaseId, body }) => {
			const { data } = await apiClient.post<BadcaseRegressionWritebackResponse>(
				`/admin/v1/eval/badcases/${badcaseId}/writeback/regression`,
				body,
			);
			return data;
		},
		onSuccess: (response) => {
			const updated = response.badcase;
			queryClient.setQueryData<BadcaseListResponse>(
				["admin", "eval", "badcases", merchantId, limit],
				(previous) =>
					previous
						? {
								...previous,
								items: previous.items.map((item) =>
									item.badcase_id === updated.badcase_id ? updated : item,
								),
							}
						: previous,
			);
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "badcases", updated.badcase_id, "events"],
			});
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "regression-samples", merchantId],
			});
		},
	});
}

export function useRegressionBatches(
	merchantId = DEFAULT_EVAL_MERCHANT_ID,
	targetDatasetId = "regression_badcase",
	limit = 5,
) {
	return useQuery<EvalRegressionBatchListResponse>({
		queryKey: ["admin", "eval", "regression-batches", merchantId, targetDatasetId, limit],
		queryFn: async () => {
			const { data } = await apiClient.get<EvalRegressionBatchListResponse>(
				"/admin/v1/eval/regression-batches",
				{
					params: {
						merchant_id: merchantId,
						target_dataset_id: targetDatasetId,
						limit,
					},
				},
			);
			return data;
		},
		staleTime: 30 * 1000,
	});
}

export function useScheduleRegressionBatch() {
	const queryClient = useQueryClient();
	return useMutation<EvalRegressionBatchItem, unknown, EvalRegressionBatchCreateRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<EvalRegressionBatchItem>(
				"/admin/v1/eval/regression-batches",
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "regression-batches"],
			});
		},
	});
}

export function useExecuteRegressionBatch() {
	const queryClient = useQueryClient();
	return useMutation<
		EvalRegressionBatchExecuteResponse,
		unknown,
		{ batchId: string; body: EvalRegressionBatchExecuteRequest }
	>({
		mutationFn: async ({ batchId, body }) => {
			const { data } = await apiClient.post<EvalRegressionBatchExecuteResponse>(
				`/admin/v1/eval/regression-batches/${encodeURIComponent(batchId)}/execute`,
				body,
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "regression-batches"],
			});
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "runs"],
			});
		},
	});
}

export function useCloseRegressionBatchBadcases(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	const queryClient = useQueryClient();
	return useMutation<
		EvalRegressionBatchCloseBadcasesResponse,
		unknown,
		{ batchId: string; body: EvalRegressionBatchCloseBadcasesRequest }
	>({
		mutationFn: async ({ batchId, body }) => {
			const { data } = await apiClient.post<EvalRegressionBatchCloseBadcasesResponse>(
				`/admin/v1/eval/regression-batches/${encodeURIComponent(batchId)}/close-badcases`,
				body,
			);
			return data;
		},
		onSuccess: (response) => {
			const closedBadcaseIds = new Set(
				response.items
					.filter((item) => item.decision === "closed" && item.badcase_id)
					.map((item) => item.badcase_id as string),
			);
			if (closedBadcaseIds.size > 0) {
				queryClient.setQueryData<BadcaseListResponse>(
					["admin", "eval", "badcases", merchantId, limit],
					(previous) =>
						previous
							? {
									...previous,
									items: previous.items.map((item) =>
										closedBadcaseIds.has(item.badcase_id)
											? { ...item, status: "closed", updated_at: new Date().toISOString() }
											: item,
									),
								}
							: previous,
				);
			}
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "badcases"],
			});
			queryClient.invalidateQueries({
				queryKey: ["admin", "eval", "regression-batches"],
			});
		},
	});
}

export function useAutoCloseBadcase(merchantId = DEFAULT_EVAL_MERCHANT_ID, limit = 20) {
	const queryClient = useQueryClient();
	return useMutation<BadcaseItem, unknown, { badcaseId: string; body: BadcaseAutoCloseRequest }>({
		mutationFn: async ({ badcaseId, body }) => {
			const { data } = await apiClient.post<BadcaseItem>(
				`/admin/v1/eval/badcases/${badcaseId}/auto-close`,
				body,
			);
			return data;
		},
		onSuccess: (updated, variables) => {
			queryClient.setQueryData<BadcaseListResponse>(
				["admin", "eval", "badcases", merchantId, limit],
				(previous) =>
					previous
						? {
								...previous,
								items: previous.items.map((item) =>
									item.badcase_id === updated.badcase_id ? updated : item,
								),
							}
						: previous,
			);
			queryClient.setQueryData<BadcaseEventListResponse>(
				["admin", "eval", "badcases", updated.badcase_id, "events"],
				(previous) => {
					const note =
						variables.body.close_note ?? "自动关闭：复跑通过且已写回回归集，进入关闭状态。";
					const writebackEvent: BadcaseEventItem | null = updated.graduation_dataset_id
						? {
								event_id: `bce_${updated.badcase_id}_regression_writeback`,
								badcase_id: updated.badcase_id,
								merchant_id: updated.merchant_id,
								event_type: "regression_writeback",
								from_status: "fixed",
								to_status: "graduated",
								rerun_status: null,
								actor: updated.graduated_by,
								event_note: `Badcase 已写回回归集：${updated.graduation_dataset_id}`,
								trace_id: updated.trace_id,
								admin_run_id: updated.admin_run_id,
								created_at: updated.graduated_at ?? updated.updated_at,
							}
						: null;
					const autoCloseEvent: BadcaseEventItem = {
						event_id: `bce_${updated.badcase_id}_auto_closed`,
						badcase_id: updated.badcase_id,
						merchant_id: updated.merchant_id,
						event_type: "status_changed",
						from_status: "graduated",
						to_status: "closed",
						rerun_status: null,
						actor: variables.body.updated_by ?? null,
						event_note: note,
						trace_id: updated.trace_id,
						admin_run_id: updated.admin_run_id,
						created_at: updated.updated_at,
					};
					const existingItems = previous?.items ?? [];
					const withoutDuplicate = existingItems.filter(
						(item) =>
							item.event_id !== autoCloseEvent.event_id &&
							item.event_id !== writebackEvent?.event_id,
					);
					const appended = writebackEvent
						? [...withoutDuplicate, writebackEvent, autoCloseEvent]
						: [...withoutDuplicate, autoCloseEvent];
					return {
						badcase_id: previous?.badcase_id ?? updated.badcase_id,
						items: appended,
						total: appended.length,
					};
				},
			);
		},
	});
}

// ─── L0 CX Score 体验打分（W5） ──────────────────────────────────────────

export interface CxScoreConversationTurn {
	role: "user" | "agent";
	content: string;
}

export interface CxScoreRunRequest {
	conversation: CxScoreConversationTurn[];
	source?: string;
	meta?: Record<string, unknown>;
}

export interface CxScoreRunResponse {
	scored: boolean;
	eligible: boolean;
	unscored_reason: string | null;
	cx_score: number | null;
	reasons: Record<string, string> | null;
	explanation: string | null;
	compliance_risk: string | null;
	compliance_capped: boolean;
	model: string;
	source: string;
}

export function useRunCxScore() {
	return useMutation<CxScoreRunResponse, unknown, CxScoreRunRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<CxScoreRunResponse>(
				"/admin/v1/eval/cx-score/run",
				body,
			);
			return data;
		},
	});
}

// ─── Golden15 主链路评测（W7 · 前端按钮，接已有 /golden-15/run runner）──────

export type Golden15RunResponse = ApiSchemas["Golden15RunResponse"];
export type Golden15RunRequest = ApiSchemas["Golden15RunRequest"];

export function useRunGolden15() {
	return useMutation<Golden15RunResponse, unknown, Golden15RunRequest>({
		mutationFn: async (body) => {
			const { data } = await apiClient.post<Golden15RunResponse>(
				"/admin/v1/eval/golden-15/run",
				body,
			);
			return data;
		},
	});
}
